import { formatDay } from '../game/daily';
import type { Progress } from '../game/types';
import { must } from './dom';

export interface ArchiveRow {
  day: number;
  date: string;
  /** `null` when the grid hasn't been completed. */
  score: { mw: number; tier: string } | null;
  /** The reference was studied before this grid was perfected (GDD §4.3). */
  studied: boolean;
  /** This is the grid currently on the board. */
  current: boolean;
}

/** Newest grid first, all the way back to #1. */
export function buildArchiveRows(
  progress: Progress,
  today: number,
  currentDay: number | null,
): ArchiveRow[] {
  const rows: ArchiveRow[] = [];
  for (let d = today; d >= 1; d--) {
    const r = progress.results[d];
    rows.push({
      day: d,
      date: formatDay(d),
      score: r ? { mw: r.mw, tier: r.tier ?? '' } : null,
      studied: Boolean(progress.runs[d]?.studied),
      current: d === currentDay,
    });
  }
  return rows;
}

/**
 * Stored results keep the tier emoji (it is the share's medal); the UI shows
 * the rank as plain text instead.
 */
const RANK: Record<string, string> = {
  '🏆': 'Superconductor',
  '🥇': 'Master',
  '🥈': 'Journeyman',
  '🥉': 'Apprentice',
};

/** Render the archive list and hand back each row's day on click. */
export function renderArchive(rows: ArchiveRow[], onPick: (day: number) => void): void {
  const list = must('archList');
  list.innerHTML = rows
    .map((row) => {
      const rank = row.score ? (RANK[row.score.tier] ?? '') : '';
      // The studied mark is a word, not an emoji: no emoji renders inside
      // this interface, and 📖 belongs to the share (STYLEGUIDE §6).
      const studied = row.studied ? `<span class="studied">studied</span>` : '';
      const right = row.score
        ? `<span class="score">${studied}<b>${row.score.mw}</b> MW${rank ? ' · ' + rank : ''}</span>`
        : `<span class="unplayed">play</span>`;
      return (
        `<button class="arch-row${row.current ? ' current' : ''}" data-day="${row.day}">` +
        `<span><span class="num">#${row.day}</span> · ${row.date}</span>${right}</button>`
      );
    })
    .join('');

  list.querySelectorAll<HTMLButtonElement>('.arch-row').forEach((b) => {
    b.addEventListener('click', () => onPick(Number(b.dataset.day)));
  });
}
